import {
  BgColorsOutlined,
  CheckOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { Modal, Tooltip } from "antd";
import React, { startTransition, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DASHBOARD_LAYOUTS,
  useDashboardLayout,
} from "../../store/context/dashboard-layout-context";
import { useTenant } from "../../store/context/tenant-context";
import { darkenHex, hexToRgba } from "../../utils/helper/colorUtils";

const PreviewFrame = ({ primary, variant }) => {
  const darkPrimary = darkenHex(primary, 0.45);
  const isGlass = variant === DASHBOARD_LAYOUTS.GLASSMORPHISM;
  const isModern = variant === DASHBOARD_LAYOUTS.MODERN_GRADIENT;
  const isMinimal = variant === DASHBOARD_LAYOUTS.MINIMAL;
  const background = isGlass
    ? "linear-gradient(135deg,#0a1628,#111827,#1a1a2e)"
    : "#f9fafb";
  const sidebarBackground = isGlass ? "rgba(0,0,0,0.3)" : darkPrimary;
  const cardBackground = isGlass ? "rgba(255,255,255,0.08)" : "#fff";
  const cardBorder = isGlass
    ? "1px solid rgba(255,255,255,0.1)"
    : "1px solid #e5e7eb";
  const heroBackground = isGlass
    ? "rgba(255,255,255,0.1)"
    : isModern
      ? `linear-gradient(135deg, ${darkPrimary}, ${darkenHex(primary, 0.72)}, ${primary})`
      : isMinimal
        ? darkPrimary
        : "#e5e7eb";

  return (
    <div
      className="flex h-full w-full gap-2 rounded-lg p-2"
      style={{ background }}
    >
      <div
        className="h-full w-1/4 rounded-md"
        style={{ background: sidebarBackground }}
      />
      <div className="flex flex-1 flex-col gap-1.5">
        <div
          className={isMinimal || isModern || isGlass ? "h-10 rounded-md" : "h-3 w-3/4 rounded"}
          style={{ background: heroBackground, border: isGlass ? cardBorder : undefined }}
        />
        {(isMinimal || isModern || isGlass) && (
          <div className="flex h-4 gap-1">
            {(isMinimal ? [0, 1, 2, 3, 4] : [0, 1, 2]).map((item) => (
              <div
                key={item}
                className="flex-1 rounded"
                style={{
                  background:
                    isModern && item === 0
                      ? hexToRgba(primary, 0.15)
                      : cardBackground,
                  border: cardBorder,
                }}
              />
            ))}
          </div>
        )}
        <div className="flex flex-1 gap-1.5">
          <div
            className="flex-1 rounded-md"
            style={{ background: cardBackground, border: cardBorder }}
          />
          <div
            className="w-1/3 rounded-md"
            style={{ background: cardBackground, border: cardBorder }}
          />
        </div>
      </div>
    </div>
  );
};

const getLayoutOptions = (primary) => [
  {
    key: DASHBOARD_LAYOUTS.MODERN_GRADIENT,
    label: "Modern",
    description: "Bold gradient hero card with focused account insights.",
    disabled: false,
    preview: (
      <PreviewFrame primary={primary} variant={DASHBOARD_LAYOUTS.MODERN_GRADIENT} />
    ),
  },
  {
    key: DASHBOARD_LAYOUTS.MINIMAL,
    label: "Minimal",
    description: "Clean, spacious banking with quick access to essentials.",
    disabled: false,
    preview: (
      <PreviewFrame primary={primary} variant={DASHBOARD_LAYOUTS.MINIMAL} />
    ),
  },
  {
    key: DASHBOARD_LAYOUTS.DEFAULT,
    label: "Classic",
    description: "Original dashboard layout.",
    disabled: true,
    preview: (
      <PreviewFrame primary={primary} variant={DASHBOARD_LAYOUTS.DEFAULT} />
    ),
  },
  {
    key: DASHBOARD_LAYOUTS.GLASSMORPHISM,
    label: "Dark Glass",
    description: "Immersive dark layout with frosted cards.",
    disabled: true,
    preview: (
      <PreviewFrame primary={primary} variant={DASHBOARD_LAYOUTS.GLASSMORPHISM} />
    ),
  },
];

const DashboardLayoutSwitcher = () => {
  const [open, setOpen] = useState(false);
  const { layout, changeLayout } = useDashboardLayout();
  const { t } = useTranslation();
  const tenant = useTenant();
  const primary = tenant?.theme?.colorPrimary || "#0b3d2e";
  const layoutOptions = getLayoutOptions(primary);
  const current =
    layoutOptions.find((option) => option.key === layout) ||
    layoutOptions.find((option) => option.key === DASHBOARD_LAYOUTS.MINIMAL);

  return (
    <>
      <Tooltip title={t("Change dashboard layout")}>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-200"
        >
          <BgColorsOutlined /> {current.label}
        </button>
      </Tooltip>

      <Modal
        title={
          <div>
            <p className="text-base font-bold">
              {t("Choose Dashboard Layout")}
            </p>
            <p className="mt-0.5 text-xs font-normal text-gray-400">
              {t("Your preference is saved automatically")}
            </p>
          </div>
        }
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={620}
      >
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {layoutOptions.map((option) => {
            const isActive = layout === option.key;
            return (
              <button
                key={option.key}
                type="button"
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) return;
                  setOpen(false);
                  startTransition(() => changeLayout(option.key));
                }}
                style={
                  isActive
                    ? {
                        borderColor: primary,
                        background: hexToRgba(primary, 0.06),
                      }
                    : {}
                }
                className={`relative w-full rounded-2xl border-2 p-3 text-left transition-all ${
                  option.disabled
                    ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
                    : isActive
                      ? "cursor-pointer"
                      : "cursor-pointer border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                {option.disabled && (
                  <span className="absolute right-5 top-5 z-10 inline-flex items-center gap-1 rounded-full bg-gray-800 px-2 py-1 text-[10px] font-semibold text-white">
                    <LockOutlined /> Disabled
                  </span>
                )}
                <div className="mb-3 h-24 w-full overflow-hidden rounded-xl border border-gray-100">
                  {option.preview}
                </div>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p
                      className="text-sm font-bold"
                      style={{ color: isActive ? primary : "#1f2937" }}
                    >
                      {option.label}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {option.description}
                    </p>
                  </div>
                  {isActive && !option.disabled && (
                    <span
                      className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs text-white"
                      style={{ background: primary }}
                    >
                      <CheckOutlined />
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default DashboardLayoutSwitcher;
